import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { adminApi, type Lesson, type Track, type Question } from '@/api';

const ICON_OPTIONS = ['BrainCircuit', 'Zap', 'Cpu', 'Scale', 'Users', 'Palette', 'Briefcase', 'BarChart3', 'Megaphone', 'ShoppingBag', 'ShieldAlert'];

export function LessonForm() {
  const { id } = useParams<'id'>();
  const navigate = useNavigate();
  const isNew = !id;
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [form, setForm] = useState<{
    id: string;
    trackId: string;
    title: string;
    description: string;
    icon: string;
    order?: number;
    isPro: boolean;
  }>({
    id: '',
    trackId: '',
    title: '',
    description: '',
    icon: 'Zap',
    isPro: false,
  });
  const [questions, setQuestions] = useState<Question[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [addQuestion, setAddQuestion] = useState(false);
  const [generatingQuestions, setGeneratingQuestions] = useState(false);
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);
  const [editQ, setEditQ] = useState<{
    type: string;
    prompt: string;
    options: string;
    correctAnswer: string;
    explanation: string;
    hint: string;
  } | null>(null);
  const [newQ, setNewQ] = useState<{
    type: string;
    prompt: string;
    options: string;
    correctAnswer: string;
    explanation: string;
    hint: string;
  }>({
    type: 'multiple-choice',
    prompt: '',
    options: '',
    correctAnswer: '',
    explanation: '',
    hint: '',
  });

  useEffect(() => {
    adminApi.tracks.list().then((t) => {
      setTracks(t);
      if (isNew && t.length) setForm((f) => ({ ...f, trackId: t[0]!.id }));
    }).catch(() => {});
  }, [isNew]);

  useEffect(() => {
    if (isNew) return;
    adminApi.lessons.get(id!).then((l) => {
      const lesson = l as Lesson & { trackId?: string; order?: number; isPro?: boolean };
      setForm({
        id: l.id,
        trackId: lesson.trackId ?? '',
        title: l.title,
        description: l.description,
        icon: l.icon,
        order: lesson.order,
        isPro: lesson.isPro ?? false,
      });
    }).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, [id, isNew]);

  const loadQuestions = useCallback(() => {
    if (!id) return;
    setQuestionsLoading(true);
    adminApi.lessons.questions(id)
      .then(setQuestions)
      .catch(() => setQuestions([]))
      .finally(() => setQuestionsLoading(false));
  }, [id]);

  useEffect(() => {
    if (isNew || !id) return;
    loadQuestions();
  }, [isNew, id, loadQuestions]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      if (isNew) {
        await adminApi.lessons.create({
          id: form.id,
          trackId: form.trackId,
          title: form.title,
          description: form.description,
          icon: form.icon,
          order: form.order,
          isPro: form.isPro,
        });
        navigate('/lessons');
      } else {
        await adminApi.lessons.update(id!, {
          trackId: form.trackId || undefined,
          title: form.title,
          description: form.description,
          icon: form.icon,
          order: form.order,
          isPro: form.isPro,
        });
        navigate('/lessons');
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleAddQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setError(null);
    const options = newQ.type === 'multiple-choice'
      ? newQ.options.split('\n').map((s) => s.trim()).filter(Boolean)
      : undefined;
    const correctAnswer = newQ.type === 'true-false' ? newQ.correctAnswer : newQ.correctAnswer.trim();
    try {
      await adminApi.lessons.createQuestion(id, {
        type: newQ.type,
        prompt: newQ.prompt.trim(),
        options: options?.length ? options : undefined,
        correctAnswer,
        explanation: newQ.explanation.trim(),
        hint: newQ.hint.trim() || undefined,
      });
      setNewQ({ type: 'multiple-choice', prompt: '', options: '', correctAnswer: '', explanation: '', hint: '' });
      setAddQuestion(false);
      loadQuestions();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add question');
    }
  };

  const handleDeleteLesson = async () => {
    if (!id) return;
    if (!confirm('Delete this lesson? All questions in it will be removed.')) return;
    setError(null);
    try {
      await adminApi.lessons.delete(id);
      navigate('/lessons');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete lesson');
    }
  };

  const handleDeleteQuestion = async (qId: string) => {
    if (!id) return;
    setError(null);
    try {
      await adminApi.questions.delete(qId);
      if (editingQuestionId === qId) {
        setEditingQuestionId(null);
        setEditQ(null);
      }
      loadQuestions();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete question');
    }
  };

  const normalizeQuestionType = (t: string) => {
    const map: Record<string, string> = {
      multiple_choice: 'multiple-choice',
      true_false: 'true-false',
      fill_blank: 'fill-blank',
      matching: 'matching',
    };
    return map[t] ?? t;
  };

  const handleStartEditQuestion = (q: Question) => {
    setEditingQuestionId(q.id);
    setEditQ({
      type: normalizeQuestionType(q.type),
      prompt: q.prompt,
      options: Array.isArray(q.options) ? q.options.join('\n') : '',
      correctAnswer: Array.isArray(q.correctAnswer) ? q.correctAnswer.join(', ') : String(q.correctAnswer),
      explanation: q.explanation,
      hint: (q as Question & { hint?: string }).hint ?? '',
    });
  };

  const handleCancelEditQuestion = () => {
    setEditingQuestionId(null);
    setEditQ(null);
  };

  const handleSaveEditQuestion = async (e: React.FormEvent, qId: string) => {
    e.preventDefault();
    if (!editQ) return;
    setError(null);
    const hasOptions = editQ.type === 'multiple-choice' || editQ.type === 'matching' || editQ.type === 'fill-blank';
    const options = hasOptions
      ? editQ.options.split('\n').map((s) => s.trim()).filter(Boolean)
      : undefined;
    const correctAnswer = editQ.type === 'true-false' ? editQ.correctAnswer : editQ.correctAnswer.trim();
    try {
      await adminApi.questions.update(qId, {
        type: editQ.type,
        prompt: editQ.prompt.trim(),
        options: hasOptions ? options : undefined,
        correctAnswer,
        explanation: editQ.explanation.trim(),
        hint: editQ.hint.trim() || undefined,
      });
      setEditingQuestionId(null);
      setEditQ(null);
      loadQuestions();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update question');
    }
  };

  const handleMoveQuestion = async (index: number, direction: 'up' | 'down') => {
    if (!id) return;
    const newItems = [...questions];
    const swap = direction === 'up' ? index - 1 : index + 1;
    if (swap < 0 || swap >= newItems.length) return;
    [newItems[index], newItems[swap]] = [newItems[swap]!, newItems[index]!];
    const ids = newItems.map((q) => q.id);
    try {
      await adminApi.lessons.reorderQuestions(id, ids);
      setQuestions(newItems);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reorder failed');
    }
  };

  const handleGenerateQuestions = async () => {
    if (!id) return;
    setError(null);
    setGeneratingQuestions(true);
    try {
      const generated = await adminApi.ai.generateQuestions(form.title, form.description, 3);
      for (const q of generated) {
        await adminApi.lessons.createQuestion(id, {
          type: q.type,
          prompt: q.prompt,
          options: q.options,
          correctAnswer: q.correctAnswer,
          explanation: q.explanation,
          hint: q.hint,
        });
      }
      loadQuestions();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate questions');
    } finally {
      setGeneratingQuestions(false);
    }
  };

  if (loading) return <div className="text-slate-500">Loading…</div>;

  return (
    <div>
      <div className="mb-6 flex items-center gap-4">
        <Link to="/lessons" className="text-slate-600 hover:text-slate-900 text-sm">
          ← Lessons
        </Link>
        <h1 className="text-2xl font-semibold text-slate-800">{isNew ? 'New lesson' : 'Edit lesson'}</h1>
      </div>
      <form onSubmit={handleSubmit} className="max-w-xl space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">ID</label>
          <input
            value={form.id}
            onChange={(e) => setForm((f) => ({ ...f, id: e.target.value }))}
            required
            disabled={!isNew}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 disabled:bg-slate-100"
            placeholder="e.g. llm-101"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Track</label>
          <select
            value={form.trackId}
            onChange={(e) => setForm((f) => ({ ...f, trackId: e.target.value }))}
            required
            className="w-full rounded-lg border border-slate-300 px-3 py-2"
          >
            <option value="">Select track</option>
            {tracks.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
          <input
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            required
            className="w-full rounded-lg border border-slate-300 px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            required
            rows={3}
            className="w-full rounded-lg border border-slate-300 px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Icon</label>
          <select
            value={form.icon}
            onChange={(e) => setForm((f) => ({ ...f, icon: e.target.value }))}
            className="w-full rounded-lg border border-slate-300 px-3 py-2"
          >
            {[...new Set([...ICON_OPTIONS, form.icon])].sort().map((i) => (
              <option key={i} value={i}>{i}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <input
            id="isPro"
            type="checkbox"
            checked={form.isPro}
            onChange={(e) => setForm((f) => ({ ...f, isPro: e.target.checked }))}
            className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
          />
          <label htmlFor="isPro" className="text-sm font-medium text-slate-700">
            PRO lesson (requires subscription)
          </label>
        </div>
        {error && <div className="rounded-lg bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>}
        <div className="flex gap-3 items-center flex-wrap">
          <button
            type="submit"
            disabled={saving}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-white font-medium hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          <Link to="/lessons" className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 hover:bg-slate-50">
            Cancel
          </Link>
          {!isNew && id && (
            <button
              type="button"
              onClick={handleDeleteLesson}
              disabled={saving}
              className="rounded-lg border border-red-200 px-4 py-2 text-red-600 font-medium hover:bg-red-50 disabled:opacity-50 ml-auto"
            >
              Delete lesson
            </button>
          )}
        </div>
      </form>

      {!isNew && id && (
        <div className="mt-10 max-w-2xl">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h2 className="text-lg font-semibold text-slate-800">Questions</h2>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleGenerateQuestions}
                disabled={generatingQuestions}
                className="rounded-lg bg-indigo-100 px-3 py-1.5 text-sm font-medium text-indigo-700 hover:bg-indigo-200 disabled:opacity-50"
              >
                {generatingQuestions ? 'Generating…' : 'Generate with AI'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setAddQuestion((a) => !a);
                  if (!addQuestion) {
                    setEditingQuestionId(null);
                    setEditQ(null);
                  }
                }}
                className="rounded-lg bg-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-300"
              >
                {addQuestion ? 'Cancel' : 'Add question'}
              </button>
            </div>
          </div>
          {questionsLoading ? (
            <div className="text-slate-500 text-sm">Loading…</div>
          ) : (
            <>
              {questions.length === 0 && !addQuestion && (
                <p className="text-slate-500 text-sm">No questions yet. Add one to get started.</p>
              )}
              {questions.length > 0 && (
                <ul className="space-y-3">
                  {questions.map((q, idx) => (
                    <li
                      key={q.id}
                      className={`rounded-lg border bg-white overflow-hidden ${
                        editingQuestionId === q.id ? 'border-indigo-300 ring-1 ring-indigo-200' : 'border-slate-200'
                      }`}
                    >
                      {editingQuestionId === q.id && editQ ? (
                        <form
                          onSubmit={(e) => handleSaveEditQuestion(e, q.id)}
                          className="p-4 space-y-3 bg-slate-50"
                        >
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
                            <select
                              value={editQ.type}
                              onChange={(e) => setEditQ((f) => f && { ...f, type: e.target.value })}
                              className="w-full rounded border border-slate-300 px-3 py-2"
                            >
                              <option value="multiple-choice">multiple-choice</option>
                              <option value="true-false">true-false</option>
                              <option value="matching">matching</option>
                              <option value="fill-blank">fill-blank</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Prompt</label>
                            <input
                              value={editQ.prompt}
                              onChange={(e) => setEditQ((f) => f && { ...f, prompt: e.target.value })}
                              required
                              className="w-full rounded border border-slate-300 px-3 py-2"
                            />
                          </div>
                          {(editQ.type === 'multiple-choice' || editQ.type === 'matching' || editQ.type === 'fill-blank') && (
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1">
                                Options (one per line)
                              </label>
                              <textarea
                                value={editQ.options}
                                onChange={(e) => setEditQ((f) => f && { ...f, options: e.target.value })}
                                rows={4}
                                className="w-full rounded border border-slate-300 px-3 py-2 font-mono text-sm"
                                placeholder={editQ.type === 'multiple-choice' ? 'Option A\nOption B\nOption C' : 'One option per line'}
                              />
                            </div>
                          )}
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Correct answer</label>
                            {editQ.type === 'true-false' ? (
                              <select
                                value={editQ.correctAnswer}
                                onChange={(e) => setEditQ((f) => f && { ...f, correctAnswer: e.target.value })}
                                required
                                className="w-full rounded border border-slate-300 px-3 py-2"
                              >
                                <option value="">Select</option>
                                <option value="true">true</option>
                                <option value="false">false</option>
                              </select>
                            ) : (
                              <input
                                value={editQ.correctAnswer}
                                onChange={(e) => setEditQ((f) => f && { ...f, correctAnswer: e.target.value })}
                                required
                                placeholder="Exact match from options"
                                className="w-full rounded border border-slate-300 px-3 py-2"
                              />
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Explanation</label>
                            <textarea
                              value={editQ.explanation}
                              onChange={(e) => setEditQ((f) => f && { ...f, explanation: e.target.value })}
                              required
                              rows={2}
                              className="w-full rounded border border-slate-300 px-3 py-2"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Hint (optional)</label>
                            <textarea
                              value={editQ.hint}
                              onChange={(e) => setEditQ((f) => f && { ...f, hint: e.target.value })}
                              placeholder="Hint shown when user asks for help"
                              rows={2}
                              className="w-full rounded border border-slate-300 px-3 py-2"
                            />
                          </div>
                          <div className="flex gap-2">
                            <button
                              type="submit"
                              className="rounded-lg bg-indigo-600 px-4 py-2 text-white text-sm font-medium hover:bg-indigo-700"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={handleCancelEditQuestion}
                              className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 text-sm hover:bg-slate-100"
                            >
                              Cancel
                            </button>
                          </div>
                        </form>
                      ) : (
                        <div className="p-4 flex items-start justify-between gap-4">
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-slate-800">{q.prompt}</p>
                            <p className="text-sm text-slate-500 mt-1">
                              {q.type} · correct: {Array.isArray(q.correctAnswer) ? q.correctAnswer.join(', ') : q.correctAnswer}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleStartEditQuestion(q)}
                              className="rounded border border-slate-300 px-2 py-1 text-sm text-slate-700 hover:bg-slate-50"
                            >
                              Edit
                            </button>
                            <div className="flex gap-1">
                              <button
                                type="button"
                                onClick={() => handleMoveQuestion(idx, 'up')}
                                disabled={idx === 0}
                                className="rounded border border-slate-300 px-2 py-0.5 text-xs disabled:opacity-40 hover:bg-slate-50"
                                title="Move up"
                              >
                                ↑
                              </button>
                              <button
                                type="button"
                                onClick={() => handleMoveQuestion(idx, 'down')}
                                disabled={idx === questions.length - 1}
                                className="rounded border border-slate-300 px-2 py-0.5 text-xs disabled:opacity-40 hover:bg-slate-50"
                                title="Move down"
                              >
                                ↓
                              </button>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleDeleteQuestion(q.id)}
                              className="rounded border border-red-200 px-2 py-1 text-sm text-red-600 hover:bg-red-50"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
          {addQuestion && (
            <form onSubmit={handleAddQuestion} className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
                <select
                  value={newQ.type}
                  onChange={(e) => setNewQ((f) => ({ ...f, type: e.target.value }))}
                  className="w-full rounded border border-slate-300 px-3 py-2"
                >
                  <option value="multiple-choice">multiple-choice</option>
                  <option value="true-false">true-false</option>
                  <option value="matching">matching</option>
                  <option value="fill-blank">fill-blank</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Prompt</label>
                <input
                  value={newQ.prompt}
                  onChange={(e) => setNewQ((f) => ({ ...f, prompt: e.target.value }))}
                  required
                  className="w-full rounded border border-slate-300 px-3 py-2"
                />
              </div>
              {newQ.type === 'multiple-choice' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Options (one per line)</label>
                  <textarea
                    value={newQ.options}
                    onChange={(e) => setNewQ((f) => ({ ...f, options: e.target.value }))}
                    rows={4}
                    className="w-full rounded border border-slate-300 px-3 py-2 font-mono text-sm"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Correct answer</label>
                {newQ.type === 'true-false' ? (
                  <select
                    value={newQ.correctAnswer}
                    onChange={(e) => setNewQ((f) => ({ ...f, correctAnswer: e.target.value }))}
                    required
                    className="w-full rounded border border-slate-300 px-3 py-2"
                  >
                    <option value="">Select</option>
                    <option value="true">true</option>
                    <option value="false">false</option>
                  </select>
                ) : (
                  <input
                    value={newQ.correctAnswer}
                    onChange={(e) => setNewQ((f) => ({ ...f, correctAnswer: e.target.value }))}
                    required
                    placeholder="Exact match from options"
                    className="w-full rounded border border-slate-300 px-3 py-2"
                  />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Explanation</label>
                <textarea
                  value={newQ.explanation}
                  onChange={(e) => setNewQ((f) => ({ ...f, explanation: e.target.value }))}
                  required
                  rows={2}
                  className="w-full rounded border border-slate-300 px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Hint (optional)</label>
                <textarea
                  value={newQ.hint}
                  onChange={(e) => setNewQ((f) => ({ ...f, hint: e.target.value }))}
                  placeholder="Hint shown when user asks for help"
                  rows={2}
                  className="w-full rounded border border-slate-300 px-3 py-2"
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-white text-sm font-medium hover:bg-indigo-700"
                >
                  Add question
                </button>
                <button
                  type="button"
                  onClick={() => setAddQuestion(false)}
                  className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 text-sm hover:bg-slate-100"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
